import { Button, ModalV2 } from '@components'
import { Switch } from '@components/ui/Primitives'
import { useBusinessContext } from '@context'
import { setLucaPaygConsent } from '@query'
import { getApiErrorMessage } from '@utils'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaChartLine, FaInfoCircle } from 'react-icons/fa'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled, { useTheme } from 'styled-components'

interface Props {
  isVisible: boolean
  onClose: () => void
}

export const LucaPaygConsentModal: React.FC<Props> = ({ isVisible, onClose }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { data: business } = useBusinessContext()
  const saved = Boolean(business?.luca_payg_allowed)

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      header={t('settings.billing.lucaUsage.paygModal.title')}
      headerIcon={<FaChartLine color={theme.colors.primary} />}
      width={480}
      autoHeight
    >
      <PaygConsentForm saved={saved} onClose={onClose} />
    </ModalV2>
  )
}

const PaygConsentForm: React.FC<{ saved: boolean; onClose: () => void }> = ({ saved, onClose }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [allowed, setAllowed] = useState(saved)

  const updateMutation = useMutation<{ luca_payg_allowed: boolean }, unknown, boolean>({
    mutationFn: next => setLucaPaygConsent({ slug }, { luca_payg_allowed: next }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug] })
      await queryClient.invalidateQueries({ queryKey: [slug, 'luca-usage'] })
      await queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('widget-session')
      })
      toast.success(t('settings.billing.lucaUsage.paygModal.success'))
      onClose()
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, t('settings.billing.lucaUsage.paygModal.error')))
    }
  })

  return (
    <>
      <ModalBody>
        <Description>{t('settings.billing.lucaUsage.paygModal.description')}</Description>
        <ToggleCard>
          <ToggleLabel>{t('settings.billing.lucaUsage.paygModal.toggle')}</ToggleLabel>
          <Switch
            checked={allowed}
            onChange={event => setAllowed(event.target.checked)}
            disabled={updateMutation.isPending}
            aria-label={t('settings.billing.lucaUsage.paygModal.toggle')}
          />
        </ToggleCard>
        <Info>
          <FaInfoCircle />
          <span>
            {t(
              allowed
                ? 'settings.billing.lucaUsage.paygModal.on'
                : 'settings.billing.lucaUsage.paygModal.off'
            )}
          </span>
        </Info>
      </ModalBody>
      <FooterActions>
        <Button isSecondary onClick={onClose} disabled={updateMutation.isPending}>
          {t('general.cancel')}
        </Button>
        <Button
          intent="primary"
          onClick={() => updateMutation.mutate(allowed)}
          disabled={updateMutation.isPending}
          showSpinner={updateMutation.isPending}
        >
          {t('settings.billing.lucaUsage.paygModal.save')}
        </Button>
      </FooterActions>
    </>
  )
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const Description = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.luca.textMuted};
`

const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 2px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;

  & > label {
    margin: 0;
  }
`

const ToggleLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.luca.navy};
`

const Info = styled.p`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.iconGray};

  svg {
    flex-shrink: 0;
    margin-top: 3px;
  }
`

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
